<script lang="ts">
  import {
    mdiCalendarBlankOutline,
    mdiClipboardTextOutline,
    mdiClipboardPlusOutline,
  } from "@mdi/js"
  import { untrack } from "svelte"
  import ToggleCTA from "@kontextwerk/web-sdk/ui/actions/ToggleCTA.svelte"
  import { fetchOrderTypes } from "../../../../../../../../../api/kehrblatt/order-types"
  import { fetchNextWorkOrderNumber } from "../../../../../../../../../api/kehrblatt/work-orders"
  import CreateWorkflowActionBox from "../../../create-workflow-action-box"
  import CreateWorkflowStepFrame from "../../../create-workflow-step-frame"
  import StepActionCTA from "../../../../../../../_shared/_modules/step-action-cta"
  import WorkOrderFormFields from "../../../../../../../_shared/_modules/work-order-form-fields"
  import type { WorkOrderFormValue } from "../../../../../../../_shared/_logic/work-order-form-types"
  import WorkOrderAsyncSelect from "../../../../../../../_shared/_modules/work-order-async-select"
  import {
    buildWorkOrderSelectionFromProjection,
    isWorkOrderSelectableForAppointmentCreate,
    type WorkOrderSelectSelection,
  } from "../../../../../../../_shared/_logic/work-orders-selection"
  import type {
    AppointmentCreateInit,
    AppointmentGeneralOutput,
    AppointmentCreateWorkflowEvent,
    AppointmentWorkOrderSource,
  } from "../../../../_logic/create-appointment-contract"
  import {
    appointmentWorkOrderFieldIds,
    createAppointmentWorkOrderDraft,
    normalizeId,
    validateAppointmentWorkOrderDraft,
  } from "../../_shared/_logic/work-order-draft"

  type Props = {
    init: AppointmentCreateInit
    output?: AppointmentGeneralOutput
    readOnly?: boolean
    canSwitchToBlocker?: boolean
    onEvent: (event: AppointmentCreateWorkflowEvent) => void | Promise<void>
    onError?: (message: string | null) => void
  }

  const {
    init,
    output = undefined,
    readOnly = false,
    canSwitchToBlocker = false,
    onEvent,
    onError = () => {},
  }: Props = $props()

  const workOrderEmployeeOptions = $derived(
    init.employees.map((employee) => ({
      value: employee.id,
      label: employee.label,
    })),
  )
  const propertyById = $derived(
    new Map((init.properties ?? []).map((property) => [normalizeId(property.id), property])),
  )
  const selectionFromInitWorkOrder = (workOrderId: string | null | undefined) => {
    const normalizedWorkOrderId = normalizeId(workOrderId)

    if (!normalizedWorkOrderId) return null

    const workOrder =
      init.workOrders.find((entry) => normalizeId(entry.id) === normalizedWorkOrderId) ?? null

    if (!workOrder) return null

    if (
      !isWorkOrderSelectableForAppointmentCreate({ workOrder, scheduleEvents: init.scheduleEvents })
    )
      return null

    return buildWorkOrderSelectionFromProjection({
      workOrder,
      property: propertyById.get(normalizeId(workOrder.property_id)) ?? null,
    })
  }

  const initialGeneral = untrack(() => {
    const workOrderId = output?.workOrderId ?? init.defaultWorkOrderId ?? ""
    const outputSelection = output?.selectedWorkOrder ?? null
    const defaultSource =
      init.allowWorkOrderSelection === false
        ? "none"
        : (output?.workOrderSource ??
          (init.allowNewWorkOrder ? (init.defaultWorkOrderSource ?? "existing") : "existing"))

    return {
      workOrderSource: defaultSource,
      workOrderId,
      selectedWorkOrder:
        outputSelection && normalizeId(outputSelection.id) === normalizeId(workOrderId)
          ? outputSelection
          : selectionFromInitWorkOrder(workOrderId),
      newWorkOrderDraft:
        output?.newWorkOrderDraft ??
        createAppointmentWorkOrderDraft({
          defaultOrderNumber: init.defaultOrderNumber,
          employees: init.employees,
        }),
    }
  })

  let workOrderSource = $state<AppointmentWorkOrderSource>(initialGeneral.workOrderSource)
  let workOrderId = $state(initialGeneral.workOrderId)
  let selectedWorkOrder = $state<WorkOrderSelectSelection | null>(initialGeneral.selectedWorkOrder)
  let newWorkOrderDraft = $state<WorkOrderFormValue>(initialGeneral.newWorkOrderDraft)
  let newWorkOrderFormValid = $state(false)
  const initialCreationDefaults = untrack(() => ({
    orderTypes: init.orderTypes ?? [],
    defaultOrderNumber: init.defaultOrderNumber ?? "",
    loaded: Boolean((init.orderTypes?.length ?? 0) || normalizeId(init.defaultOrderNumber)),
  }))
  let loadedOrderTypes = $state<OrderType[]>(initialCreationDefaults.orderTypes)
  let loadedDefaultOrderNumber = $state(initialCreationDefaults.defaultOrderNumber)
  let defaultsLoaded = $state(initialCreationDefaults.loaded)
  let defaultsLoading = $state(false)
  let switchingToBlocker = $state(false)
  const orderTypesForDraft = $derived(
    loadedOrderTypes.length ? loadedOrderTypes : (init.orderTypes ?? []),
  )
  const allowWorkOrderSelection = $derived(init.allowWorkOrderSelection !== false)
  const allowFreeAppointment = $derived(
    Boolean(init.allowFreeAppointment || !allowWorkOrderSelection),
  )
  const allowNewWorkOrder = $derived(Boolean(allowWorkOrderSelection && init.allowNewWorkOrder))
  const workOrderSourceOptions = $derived.by(() => {
    const options: Array<{ label: string; value: AppointmentWorkOrderSource; icon: string }> = []

    if (allowFreeAppointment)
      options.push({ label: "Freier Termin", value: "none", icon: mdiCalendarBlankOutline })

    if (allowWorkOrderSelection) {
      options.push({
        label: "Bestehender Auftrag",
        value: "existing",
        icon: mdiClipboardTextOutline,
      })

      if (allowNewWorkOrder)
        options.push({ label: "Neuer Auftrag", value: "new", icon: mdiClipboardPlusOutline })
    }

    return options
  })
  const effectiveWorkOrderSource = $derived<AppointmentWorkOrderSource>(
    !allowWorkOrderSelection
      ? "none"
      : workOrderSource === "none" && allowFreeAppointment
        ? "none"
        : allowNewWorkOrder
          ? workOrderSource
          : "existing",
  )
  const newWorkOrderValidationMessage = $derived(
    validateAppointmentWorkOrderDraft({
      data: newWorkOrderDraft,
      properties: init.properties,
      orderTypes: orderTypesForDraft,
      formValid: newWorkOrderFormValid,
    }),
  )
  const validationMessage = $derived.by(() => {
    if (effectiveWorkOrderSource === "none") {
      return null
    } else if (effectiveWorkOrderSource === "new") {
      if (newWorkOrderValidationMessage) return newWorkOrderValidationMessage
    } else if (!workOrderId || normalizeId(selectedWorkOrder?.id) !== normalizeId(workOrderId)) {
      return "Bitte einen Auftrag auswählen."
    }

    return null
  })
  const canComplete = $derived(!validationMessage)

  const loadWorkOrderCreationDefaults = async () => {
    if (defaultsLoading || defaultsLoaded || !init.organizationId) return

    defaultsLoading = true
    try {
      const [orderTypesResponse, nextNumberResponse] = await Promise.all([
        loadedOrderTypes.length
          ? Promise.resolve(loadedOrderTypes)
          : fetchOrderTypes(init.organizationId),
        loadedDefaultOrderNumber
          ? Promise.resolve({ next_order_number: loadedDefaultOrderNumber })
          : fetchNextWorkOrderNumber(init.organizationId),
      ])
      loadedOrderTypes = orderTypesResponse ?? []
      const nextOrderNumber = nextNumberResponse?.next_order_number ?? ""
      loadedDefaultOrderNumber = nextOrderNumber

      if (nextOrderNumber && !normalizeId(newWorkOrderDraft.order_number)) {
        newWorkOrderDraft = { ...newWorkOrderDraft, order_number: nextOrderNumber }
      }
    } catch (error) {
      console.error("appointment_create_work_order_defaults_load_failed", error)
    } finally {
      defaultsLoaded = true
      defaultsLoading = false
    }
  }

  $effect(() => {
    if (effectiveWorkOrderSource === "new") {
      void loadWorkOrderCreationDefaults()
    }
  })

  const handleWorkOrderChange = (selection: WorkOrderSelectSelection | null) => {
    selectedWorkOrder = selection
    workOrderId = normalizeId(selection?.id)
  }

  const handleNewWorkOrderChange = ({
    data,
    valid,
  }: {
    data: WorkOrderFormValue
    valid: boolean
  }) => {
    newWorkOrderDraft = data
    newWorkOrderFormValid = valid
  }

  const validateNewWorkOrderData = ({
    data,
  }: {
    data: WorkOrderFormValue
    form: HTMLFormElement | null
  }) =>
    !validateAppointmentWorkOrderDraft({
      data,
      properties: init.properties,
      orderTypes: orderTypesForDraft,
      formValid: true,
    })

  const complete = () => {
    onError(null)

    if (validationMessage) {
      onError(validationMessage)

      return
    }

    void onEvent({
      type: "general.completed",
      output: {
        workOrderSource: effectiveWorkOrderSource,
        workOrderId: effectiveWorkOrderSource === "existing" ? workOrderId : "",
        selectedWorkOrder: effectiveWorkOrderSource === "existing" ? selectedWorkOrder : null,
        newWorkOrderDraft: effectiveWorkOrderSource === "new" ? newWorkOrderDraft : null,
      },
    })
  }

  const switchToBlocker = async () => {
    if (switchingToBlocker) return

    onError(null)
    switchingToBlocker = true
    try {
      await onEvent({ type: "switch_to_blocker" })
    } finally {
      switchingToBlocker = false
    }
  }
</script>

<CreateWorkflowStepFrame title="Auftrag" description="" helpModalTextId="appointmentCreateGeneral">
  {#if workOrderSourceOptions.length > 1}
    <div class="source-toggle-row">
      <ToggleCTA
        value={workOrderSource}
        options={workOrderSourceOptions}
        darkMode={true}
        disabled={readOnly}
        allowReselect={false}
        onChange={(value) => {
          workOrderSource = value === "none" ? "none" : value === "new" ? "new" : "existing"
        }}
      />
    </div>
  {/if}
  <CreateWorkflowActionBox
    label={effectiveWorkOrderSource === "none"
      ? "Freier Termin"
      : effectiveWorkOrderSource === "existing"
        ? "Auftrag auswählen"
        : "Auftrag anlegen"}
    actions={!readOnly ? actionSnippet : null}
  >
    {#if effectiveWorkOrderSource === "none"}
      <p>Der Termin wird ohne Auftragsverknüpfung angelegt.</p>
    {:else if effectiveWorkOrderSource === "existing"}
      <p>
        Ein bestehender Auftrag übernimmt Liegenschaft, Auftragsart, zugelassene Mitarbeitende und
        typische Dauer.
      </p>
      <div class="create-workflow-field-grid">
        <WorkOrderAsyncSelect
          organizationId={init.organizationId}
          label="Auftrag"
          placeholder="Auftrag auswählen"
          searchMode="appointment-create"
          requiredAccess="manage"
          bind:value={workOrderId}
          bind:selectedWorkOrder
          surfaceTone="dark"
          brightBackground={false}
          disabled={readOnly || !init.organizationId}
          onChange={handleWorkOrderChange}
        />
      </div>
    {:else}
      <p>Der neue Auftrag wird beim Speichern angelegt und direkt mit diesem Termin verbunden.</p>
      <div class="new-work-order">
        <WorkOrderFormFields
          bind:value={newWorkOrderDraft}
          bind:valid={newWorkOrderFormValid}
          organizationId={init.organizationId}
          properties={init.properties}
          customers={init.customers}
          orderTypes={orderTypesForDraft}
          defaultOrderNumber={loadedDefaultOrderNumber}
          employees={workOrderEmployeeOptions}
          brightBackground={false}
          fieldIds={appointmentWorkOrderFieldIds}
          theme="dark"
          submitLabel="Anlegen"
          showReset={false}
          showSubmit={false}
          mode="create"
          disabled={readOnly}
          validateData={validateNewWorkOrderData}
          onChange={handleNewWorkOrderChange}
        />
        {#if defaultsLoading}
          <p class="hint">Auftragsdaten werden vorbereitet…</p>
        {/if}
      </div>
    {/if}
  </CreateWorkflowActionBox>
</CreateWorkflowStepFrame>

{#snippet actionSnippet()}
  {#if canSwitchToBlocker}
    <StepActionCTA
      type="secondaryButton"
      content={switchingToBlocker ? "Öffne Blocker…" : "Blocker anlegen"}
      disabled={switchingToBlocker}
      disabledReason="Blocker-Overlay wird geöffnet."
      onClick={switchToBlocker}
    />
  {/if}
  <StepActionCTA
    type="primaryButton"
    content="Weiter zur Planung"
    disabled={!canComplete}
    disabledReason={validationMessage}
    onClick={complete}
  />
{/snippet}

<style>
  .new-work-order {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .hint {
    margin: 0;
    color: color-mix(in srgb, var(--text-100) 70%, transparent);
    line-height: 1.35;
  }

  .source-toggle-row {
    display: flex;
    justify-content: flex-end;
  }
</style>
